// Screen-space HUD drawn over the 3D scene on its own 2D canvas (z-index between the
// WebGL canvas and the DOM HUD): health bars above hurt units and expanding event
// pings (raids, combat, deaths…). Projects world positions through the camera each
// frame. Pure view over GameState. Owner: client-render3d.

import { PerspectiveCamera, Vector3 } from 'three'
import { ITEMS, UNITS, buildingFootprint, isBoxOrder } from '../../shared/constants'
import { DIRS, hexToPixel } from '../../shared/hex'
import { unitMaxHp } from '../../shared/person'
import { townHallPhase } from '../../shared/townhall'
import { HEX_SIZE, type GameState } from '../state'
import { storageStatus } from '../storage'
import { drawStorageWarning } from '../render/storageWarnings'
import { drawDiscoveries } from '../render/discoveries'
import { placementPreview, drawBuildPreviewCard } from '../render/buildPreviewCard'
import { ghostValid } from '../render/renderer'
import { resolvePier } from '../pier'
import { statusIcon } from '../render/status'
import { drawStatusBadge } from '../render/statusBadge'
import { missingWorkTool } from '../render/workerTool'
import { drawToolWarning } from '../render/toolWarning'
import { drawTreeGrowthBadge, treeGrowthStage } from '../render/treeGrowth'
import { siteStageIcon } from '../render/terrain'
import { BUILD_ICON } from '../ui/panels'
import { tileHeight } from './terrainMesh'
import type { UnitVis } from './entities3d'
import type { Building } from '../../shared/types'

const S = HEX_SIZE
const PING_COLORS: Record<string, string> = {
  raid: '#ff5040', combat: '#ff8c3c', death: '#ff5040',
  build: '#7fd46a', diplo: '#6fa8ff', info: '#d8e0e6',
}

interface Ping { x: number; y: number; z: number; t0: number; color: string }
interface FloatText { x: number; y: number; z: number; t0: number; text: string }

export class Hud3d {
  private cv = document.createElement('canvas')
  private ctx: CanvasRenderingContext2D
  private dpr = 1
  private w = 1
  private h = 1
  private pings: Ping[] = []
  private floats: FloatText[] = []
  private v = new Vector3()

  // unitAnim: per-unit final world position written by EntityLayer3d each frame, so an
  // HP bar rides exactly where its unit's mesh stands (smoothing, melee closing).
  constructor(private camera: PerspectiveCamera, st: GameState, private unitAnim: Map<number, UnitVis>) {
    this.cv.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2'
    document.body.appendChild(this.cv)
    this.ctx = this.cv.getContext('2d')!
    this.resize()
    window.addEventListener('resize', () => this.resize())
    st.on('events', (data: unknown) => {
      const list = Array.isArray(data) ? data : data ? [data] : []
      for (const ev of list as { q?: number; r?: number; kind?: string; msg?: string }[]) {
        if (ev.q === undefined || ev.r === undefined) continue
        const p = hexToPixel(ev.q, ev.r, S)
        if (ev.kind === 'decay') {
          // weathering damage floats over the building instead of pinging
          this.floats.push({ x: p.x, y: tileHeight(st, ev.q, ev.r) + 26, z: p.y, t0: performance.now(), text: ev.msg ?? '' })
          if (this.floats.length > 24) this.floats.shift()
          continue
        }
        this.pings.push({ x: p.x, y: tileHeight(st, ev.q, ev.r), z: p.y, t0: performance.now(), color: PING_COLORS[ev.kind ?? 'info'] ?? '#d8e0e6' })
        if (this.pings.length > 40) this.pings.shift()
      }
    })
  }

  private resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1)
    this.w = window.innerWidth; this.h = window.innerHeight
    this.cv.width = Math.round(this.w * this.dpr)
    this.cv.height = Math.round(this.h * this.dpr)
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
  }

  private project(x: number, y: number, z: number): { x: number; y: number; vis: boolean } {
    this.v.set(x, y, z).project(this.camera)
    return { x: (this.v.x * 0.5 + 0.5) * this.w, y: (-this.v.y * 0.5 + 0.5) * this.h, vis: this.v.z < 1 }
  }

  private bar(sx: number, sy: number, frac: number, w = 32, h = 5): void {
    const ctx = this.ctx
    ctx.fillStyle = 'rgba(6,10,14,0.6)'
    ctx.fillRect(sx - w / 2 - 1, sy - 1, w + 2, h + 2)
    ctx.fillStyle = frac > 0.5 ? '#5fd46a' : frac > 0.25 ? '#e0c34a' : '#e0503a'
    ctx.fillRect(sx - w / 2, sy, w * frac, h)
  }

  /** Name pill above the hovered unit: the serf's name (or the creature's kind). */
  private nameTag(st: GameState): void {
    const h = st.hoverEnt
    if (h?.kind !== 'unit') return
    const u = st.units.get(h.id)
    if (!u) return
    const tool = missingWorkTool(st, u)
    const name = u.person?.name ?? UNITS[u.type]?.name ?? u.type
    const label = name + (tool ? ` · Needs ${ITEMS[tool].name.toLowerCase()}` : '')
    const p = this.unitAnim.get(u.id)
    const c = hexToPixel(u.q, u.r, S)
    const s = p
      ? this.project(p.x, p.y + 30, p.z)
      : this.project(c.x, tileHeight(st, u.q, u.r) + 30, c.y)
    if (!s.vis) return
    const ctx = this.ctx
    ctx.font = '600 12px system-ui, sans-serif'
    const tw = ctx.measureText(label).width
    const padX = 7, ph = 20, r = 6
    const x = s.x - tw / 2 - padX, y = s.y - ph, w = tw + padX * 2
    ctx.fillStyle = 'rgba(8,14,20,0.82)'
    ctx.beginPath()
    ctx.roundRect(x, y, w, ph, r)
    ctx.fill()
    const fcol = st.factions.get(u.owner)?.color
    if (fcol) { // thin owner-colored keel under the pill
      ctx.fillStyle = fcol
      ctx.beginPath()
      ctx.roundRect(x + 4, y + ph - 2.5, w - 8, 2.5, 1.2)
      ctx.fill()
    }
    ctx.fillStyle = tool ? '#f4c566' : '#e8f0f6'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label, s.x, y + ph / 2 - (fcol ? 1 : 0))
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  /** While placing a producing building: float its yield (farms: % from the
   *  fertile hexes under the fields) above the ghost. */
  private buildLabel(st: GameState): void {
    const mode = st.buildMode
    if (!mode || !st.hover || !ghostValid(st, mode, st.hover.q, st.hover.r)) return
    const plan = mode === 'pier' ? resolvePier(st, st.hover.q, st.hover.r, st.pierRotate) : null
    const q = plan?.q ?? st.hover.q, r = plan?.r ?? st.hover.r
    const dir = plan?.dir ?? st.pierRotate
    const card = placementPreview(st, mode, q, r, dir)
    if (!card) return
    // Anchor above the whole plot, including a rotated farm or a pier's deck.
    const points = buildingFootprint(mode, q, r, dir).map(cell => {
      const p = hexToPixel(cell.q, cell.r, S)
      return this.project(p.x, tileHeight(st, cell.q, cell.r) + 34, p.y)
    }).filter(p => p.vis)
    if (!points.length) return
    drawBuildPreviewCard(this.ctx, card, points.reduce((sum, p) => sum + p.x, 0) / points.length,
      Math.min(...points.map(p => p.y)), this.w, this.h)
  }

  /** A small amber badge floating above the player's pending work: box orders show
   *  their kind (🪓 fell, 🪏/⏫/🏖️ terraform), and a building SITE shows the stage it
   *  needs right now — 🪓 clear, 🪏 dig, ⏫ fill, 📦 haul materials, 🔨 raise — so what a
   *  blueprint is waiting on reads at a glance. Nearest-first so closer badges win. */
  private orderIcons(st: GameState): void {
    const ctx = this.ctx
    const items: { sx: number; sy: number; z: number; icon: string; plant?: Building }[] = []
    for (const b of st.buildings.values()) {
      if (b.owner !== st.me) continue
      // Box orders (chop/dig/fill) show their kind; a building SITE shows the
      // stage it currently needs — fell / dig / fill / haul / raise.
      let icon: string | null = null
      let lift = 20
      if (isBoxOrder(b.type)) {
        icon = BUILD_ICON[b.type]
        lift = b.type === 'plant' ? (b.done ? [24, 35, 47][treeGrowthStage(b, st.tick)] : 19)
          : b.type === 'chop' ? 46 : 20
      } else if (!b.done) {
        icon = siteStageIcon(st, b)
        lift = 40 // float above the rising blueprint / survey stakes
      }
      if (!icon) continue
      const p = hexToPixel(b.q, b.r, S)
      const s = this.project(p.x, tileHeight(st, b.q, b.r) + lift, p.y)
      if (s.vis) items.push({ sx: s.x, sy: s.y, z: this.v.z, icon, plant: b.type === 'plant' ? b : undefined })
    }
    if (!items.length) return
    items.sort((a, b) => b.z - a.z)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = '13px system-ui, sans-serif'
    for (const it of items) {
      const r = 11
      if (it.plant) {
        drawTreeGrowthBadge(ctx, it.plant, st.tick, it.sx, it.sy, r)
        continue
      }
      ctx.fillStyle = 'rgba(12,18,10,0.82)'
      ctx.beginPath(); ctx.arc(it.sx, it.sy, r, 0, Math.PI * 2); ctx.fill()
      ctx.lineWidth = 1.6
      ctx.strokeStyle = '#f0a020' // amber ring = queued work
      ctx.beginPath(); ctx.arc(it.sx, it.sy, r, 0, Math.PI * 2); ctx.stroke()
      ctx.fillText(it.icon, it.sx, it.sy + 1)
    }
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  /** One small glyph over every serf saying what they're doing right now —
   *  hammering, felling, farming, hauling, dozing. Skips the hovered unit
   *  (its name tag owns that spot) and fades idle so it reads quiet. */
  private statusIcons(st: GameState): void {
    const ctx = this.ctx
    const hovered = st.hoverEnt?.kind === 'unit' ? st.hoverEnt.id : -1
    ctx.font = '13px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for (const u of st.units.values()) {
      if (u.id === hovered) continue
      const tool = missingWorkTool(st, u)
      const icon = statusIcon(st, u)
      if (!tool && !icon) continue
      const p = this.unitAnim.get(u.id)
      if (!p) continue // not rendered this frame (fogged/offscreen)
      const s = this.project(p.x, p.y + 30, p.z)
      if (!s.vis) continue
      if (tool) drawToolWarning(ctx, tool, s.x, s.y - 14)
      else if (icon) drawStatusBadge(ctx, icon, u, s.x, s.y, 13)
    }
    ctx.globalAlpha = 1
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  /** A paid hall upgrade stays visible while its workshop continues to run. */
  private hallUpgrades(st: GameState): void {
    const ctx = this.ctx
    for (const b of st.buildings.values()) {
      const upgrade = b.hallUpgrade
      if (b.owner !== st.me || b.type !== 'townhall' || !b.done || !upgrade) continue
      const p = hexToPixel(b.q, b.r, S)
      const height = [34, 36, 64, 78, 100][townHallPhase(b.hallPhase) - 1]
      const screen = this.project(p.x, tileHeight(st, b.q, b.r) + height, p.y)
      if (!screen.vis) continue
      const progress = Math.max(0, Math.min(1, (st.tick - upgrade.started) / Math.max(1, upgrade.ends - upgrade.started)))
      const label = b.paused ? 'Upgrade paused' : `Upgrading · ${Math.max(0, Math.ceil(upgrade.ends - st.tick))}s`
      ctx.font = '600 11px system-ui, sans-serif'
      ctx.textAlign = 'center'
      const width = Math.max(96, ctx.measureText(label).width + 16)
      ctx.fillStyle = 'rgba(12,24,21,.9)'
      ctx.beginPath(); ctx.roundRect(screen.x - width / 2, screen.y - 24, width, 30, 6); ctx.fill()
      ctx.fillStyle = '#edd09c'
      ctx.fillText(label, screen.x, screen.y - 10)
      ctx.fillStyle = '#526052'
      ctx.fillRect(screen.x - width / 2 + 7, screen.y - 4, width - 14, 3)
      ctx.fillStyle = '#d7b678'
      ctx.fillRect(screen.x - width / 2 + 7, screen.y - 4, (width - 14) * progress, 3)
      ctx.textAlign = 'left'
    }
  }

  /** Persistent warnings above each owned hub, even without hover/selection. */
  private storageWarnings(st: GameState): void {
    for (const b of st.buildings.values()) {
      if (b.owner !== st.me || !b.done || (b.type !== 'townhall' && b.type !== 'storehouse')) continue
      const status = storageStatus(b)
      if (status.level === 'normal') continue
      const p = hexToPixel(b.q, b.r, S)
      const height = b.type === 'townhall' ? [34, 36, 64, 78, 100][townHallPhase(b.hallPhase) - 1] : 48
      const screen = this.project(p.x, tileHeight(st, b.q, b.r) + height, p.y)
      if (!screen.vis || screen.x < -80 || screen.x > this.w + 80 || screen.y < -60 || screen.y > this.h + 60) continue
      // Leave the paid-upgrade progress pill its own line below this warning.
      drawStorageWarning(this.ctx, screen.x, screen.y - (b.hallUpgrade ? 42 : 10), status)
    }
  }

  draw(st: GameState): void {
    // Publish the hex→screen projection for DOM overlays (tutorial spotlight).
    st.projectHex = (q, r) => {
      const c = hexToPixel(q, r, S)
      return this.project(c.x, tileHeight(st, q, r) + 12, c.y)
    }
    const ctx = this.ctx
    ctx.clearRect(0, 0, this.w, this.h)
    this.orderIcons(st)
    this.statusIcons(st)
    this.nameTag(st)
    this.buildLabel(st)
    this.hallUpgrades(st)
    this.storageWarnings(st)
    drawDiscoveries(ctx, st, this.w, this.h)
    // health bars above hurt units
    for (const u of st.units.values()) {
      const max = unitMaxHp(u)
      if (u.hp >= max - 0.01) continue
      const p = this.unitAnim.get(u.id)
      const c = hexToPixel(u.q, u.r, S) // fallback: logical hex, first frame only
      const s = p
        ? this.project(p.x, p.y + 22, p.z)
        : this.project(c.x, tileHeight(st, u.q, u.r) + 22, c.y)
      if (s.vis) this.bar(s.x, s.y, Math.max(0, u.hp / max))
    }
    // rising damage floats (weathering)
    const nowF = performance.now()
    ctx.font = '700 14px system-ui, sans-serif'
    ctx.textAlign = 'center'
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const ft = this.floats[i]
      const age = (nowF - ft.t0) / 1000
      if (age >= 2) { this.floats.splice(i, 1); continue }
      const sp = this.project(ft.x, ft.y, ft.z)
      if (!sp.vis) continue
      ctx.globalAlpha = age < 1.4 ? 1 : 1 - (age - 1.4) / 0.6
      ctx.fillStyle = 'rgba(8,12,16,0.65)'
      ctx.fillText(ft.text, sp.x + 1, sp.y - age * 22 + 1)
      ctx.fillStyle = '#ff7a5c'
      ctx.fillText(ft.text, sp.x, sp.y - age * 22)
    }
    ctx.globalAlpha = 1
    ctx.textAlign = 'left'
    // expanding event pings
    const now = performance.now()
    for (let i = this.pings.length - 1; i >= 0; i--) {
      const p = this.pings[i]
      const age = (now - p.t0) / 1000
      if (age >= 1) { this.pings.splice(i, 1); continue }
      const s = this.project(p.x, p.y, p.z)
      if (!s.vis) continue
      ctx.globalAlpha = 1 - age
      ctx.strokeStyle = p.color
      ctx.lineWidth = 2.5
      ctx.beginPath(); ctx.arc(s.x, s.y, 6 + age * 40, 0, Math.PI * 2); ctx.stroke()
    }
    ctx.globalAlpha = 1
  }
}
